<template>
	<div v-if="hasEditorAccess" class="landing" :class="{ 'is-dark': isDark }">
		<!-- Theme Toggle Button -->
		<v-btn class="theme-toggle-btn" icon variant="text" @click="toggleTheme" :title="themeLabel">
			<v-icon>{{ themeIcon }}</v-icon>
		</v-btn>

		<!-- Dev-only: simulate an incoming call -->
		<v-btn
			v-if="isDev"
			class="sim-call-btn"
			:color="activeSessions.some((s) => s.simulated) ? 'error' : 'primary'"
			variant="flat"
			size="small"
			prepend-icon="mdi-phone-plus"
			@click="toggleSimulatedCall"
		>
			{{ activeSessions.some((s) => s.simulated) ? "Testanruf beenden" : "Testanruf simulieren" }}
		</v-btn>

		<!-- ===================== HERO ===================== -->
		<header class="hero">
			<!-- animated aurora background -->
			<div class="hero__aurora" aria-hidden="true">
				<span class="orb orb--1"></span>
				<span class="orb orb--2"></span>
				<span class="orb orb--3"></span>
			</div>
			<div class="hero__grid" aria-hidden="true"></div>

			<div class="hero__content">
				<div class="eyebrow">LiveReader · Voicebot Demo</div>
				<h1 class="hero__title">
					<span class="hero__title-line">iPad Ausleihe</span>
					<span class="hero__title-line hero__title-accent">Voicebot</span>
				</h1>
				<p class="hero__subtitle">
					Ein KI-Voicebot, der vollständig on-premise läuft — sämtliche Verarbeitung und alle Daten bleiben im
					Haus.
				</p>
			</div>
		</header>

		<!-- ===================== STATUS DOCK ===================== -->
		<div class="status-dock">
			<div class="status-pill" :class="{ 'is-live': activeSessions.length > 0 }">
				<span class="status-pill__dot"></span>
				{{ statusText }}
			</div>
		</div>

		<!-- ===================== CONTENT ===================== -->
		<main class="content">
			<section class="panel">
				<div class="livecall">
					<!-- Active sessions -->
					<div class="glass-card sessions">
						<div class="sessions__head">
							<v-icon size="20" color="#4cb6ac">mdi-format-list-bulleted</v-icon>
							<span>Aktive Anrufe</span>
						</div>
						<v-list v-if="activeSessions.length > 0" class="sessions__list" bg-color="transparent">
							<v-list-item
								v-for="session in activeSessions"
								:key="session.id"
								class="session-item"
								:disabled="loading"
								@click="() => sessionClick(session.id, session.label)"
							>
								<template #prepend>
									<span class="call-pulse">
										<span class="call-pulse__ring"></span>
										<span class="call-pulse__ring call-pulse__ring--delay"></span>
										<v-icon size="18" color="white">mdi-phone-incoming</v-icon>
									</span>
								</template>
								<v-list-item-title class="session-item__title">
									Anruf von {{ session.label }}
								</v-list-item-title>
								<v-list-item-subtitle>Aktiver Anruf</v-list-item-subtitle>
								<template #append>
									<v-icon
										:style="{ visibility: session.clicked ? 'visible' : 'hidden' }"
										color="#2e7d32"
									>
										mdi-check-circle
									</v-icon>
								</template>
							</v-list-item>
						</v-list>
						<div v-else class="sessions__empty">
							<v-icon size="28">mdi-phone-off</v-icon>
							<span>Keine aktiven Anrufe</span>
						</div>
					</div>

					<!-- QR codes -->
					<div v-if="TELEPHONE_NUMBERS.some((n) => n.showQr)" class="qr-block">
						<h3 class="qr-block__title">Telefonnummer</h3>

						<div class="qr-grid">
							<div
								v-for="phone in TELEPHONE_NUMBERS.filter((n) => n.showQr)"
								:key="phone.number"
								class="qr-card"
							>
								<div class="qr-card__frame">
									<vue-qrcode
										:value="`tel:+${phone.number}`"
										:options="{ width: 150, margin: 1 }"
										class="qr-card__img"
									/>
								</div>
								<div class="qr-card__label">{{ phone.label }}</div>
								<div class="qr-card__number">{{ formatPhoneNumber(phone.number) }}</div>
							</div>
						</div>
						<p class="qr-block__text mt-6">
							Scannen Sie den QR-Code oder wählen Sie die angezeigte Nummer. Sobald der Anruf angenommen
							wurde, erscheint er oben in der Liste — klicken Sie ihn an, um den zugehörigen
							Arbeitsbereich zu erstellen.
						</p>
					</div>
				</div>
			</section>
		</main>

		<!-- ===================== LOGOS ===================== -->
		<section class="logos">
			<div class="logos__inner">
				<div class="logo-chip">
					<img
						src="https://assets.livereader.com/asset/D09obpb8qWU3RWyk.svg"
						alt="LiveReader Logo"
						class="logo-chip__img"
					/>
				</div>
				<div class="logo-chip">
					<img
						src="https://assets.livereader.com/asset/RrFa265po7AH0BoQ.png"
						alt="DFKI Logo"
						class="logo-chip__img"
					/>
				</div>
			</div>
		</section>

		<!-- ===================== FOOTER ===================== -->
		<footer class="footer">
			<div class="footer__socials">
				<button v-for="item in socials" :key="item.href" class="footer__social" @click="openLink(item.href)">
					<v-icon v-if="item.icon.startsWith('mdi-')" size="22">{{ item.icon }}</v-icon>
					<img v-else :src="item.icon" alt="" />
				</button>
			</div>
			<div class="footer__copy">© {{ new Date().getFullYear() }} — <strong>LiveReader</strong></div>
		</footer>
	</div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from "vue";
import { useRouter } from "vue-router";
import { theme } from "@lr-notitia/notitia-ui";
import { useDomainStore } from "../../../store/domain";
import { UserRole } from "@lr-notitia/notitia-operator/schemas";
import VueQrcode from "@chenfengyuan/vue-qrcode";

interface AlternisSession {
	id: string;
	label: string;
	reference: string;
	clicked: boolean;
	simulated?: boolean;
}

const DOMAIN_ID = "d0daff9f-2430-4785-bf00-104ab5aaca5d"; // iPad
const TEMPLATE_WORKSPACE_ID = "2e99deef-3fe5-4fc1-8b02-10acd0ee4576";
const INSTANCE_DATASPACE_ID = "3d87d494-6e6c-43b7-aada-5115fce24f8d";

const router = useRouter();
const domainStore = useDomainStore();
const activeSessions = ref<AlternisSession[]>([]);

const loading = ref(false);
const TELEPHONE_NUMBERS: { label: string; number: string; showQr: boolean }[] = [
	{ label: "iPad Ausleihe", number: "4968543289625", showQr: true },
];

const socials = [
	{ icon: "mdi-linkedin", href: "https://www.linkedin.com/company/livereader-gmbh/" },
	{ icon: "/discord.svg", href: "https://discord.gg/M2dBMRcXTg" },
	{ icon: "mdi-github", href: "https://github.com/LiveReader" },
];

const hasEditorAccess = computed(() => {
	return domainStore.hasAccessRights(UserRole.Editor) || domainStore.hasAccessRights(UserRole.Admin);
});

const statusText = computed(() => {
	const n = activeSessions.value.length;
	if (n === 0) return "Warte auf eingehenden Anruf …";
	return n === 1 ? "1 aktiver Anruf" : `${n} aktive Anrufe`;
});

type ThemeMode = "light" | "dark" | "system";
const themeMode = ref<ThemeMode>((localStorage.getItem("themeMode") as ThemeMode) || "system");

const systemPrefersDark = ref(window.matchMedia("(prefers-color-scheme: dark)").matches);

const isDark = computed(() => {
	if (themeMode.value === "system") return systemPrefersDark.value;
	return themeMode.value === "dark";
});

// Listen for system theme changes
const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
const handleSystemThemeChange = (e: MediaQueryListEvent) => {
	systemPrefersDark.value = e.matches;
	if (themeMode.value === "system") {
		theme.vuetifyTheme.value = e.matches ? "dark" : "light";
	}
};
mediaQuery.addEventListener("change", handleSystemThemeChange);

const themeIcon = computed(() => {
	switch (themeMode.value) {
		case "light":
			return "mdi-weather-sunny";
		case "dark":
			return "mdi-weather-night";
		case "system":
			return "mdi-theme-light-dark";
	}
});

const themeLabel = computed(() => {
	switch (themeMode.value) {
		case "light":
			return "Light mode";
		case "dark":
			return "Dark mode";
		case "system":
			return "System theme";
	}
});

function toggleTheme() {
	const modes: ThemeMode[] = ["light", "dark", "system"];
	const currentIndex = modes.indexOf(themeMode.value);
	const nextIndex = (currentIndex + 1) % modes.length;
	themeMode.value = modes[nextIndex];
	localStorage.setItem("themeMode", themeMode.value);
	applyTheme();
}

function applyTheme() {
	if (themeMode.value === "system") {
		theme.vuetifyTheme.value = systemPrefersDark.value ? "dark" : "light";
	} else {
		theme.vuetifyTheme.value = themeMode.value;
	}
}

// Apply theme on load
applyTheme();

function openLink(href: string) {
	window.open(href, "_blank");
}

function formatPhoneNumber(number: string): string {
	// Format: +49 6854 32896 30
	if (number.startsWith("49")) {
		const countryCode = number.slice(0, 2);
		const areaCode = number.slice(2, 6);
		const part1 = number.slice(6, 11);
		const part2 = number.slice(11);
		return `+${countryCode} ${areaCode} ${part1} ${part2}`;
	}
	return `+${number}`;
}

// --- Dev-only call simulation (stripped from production builds) ---
const isDev = import.meta.env.DEV;
let simCounter = 0;

function toggleSimulatedCall() {
	const hasSimulated = activeSessions.value.some((s) => s.simulated);
	if (hasSimulated) {
		activeSessions.value = activeSessions.value.filter((s) => !s.simulated);
		return;
	}
	simCounter += 1;
	activeSessions.value = [
		...activeSessions.value,
		{
			id: `sim_${simCounter}`,
			label: "Testanrufer",
			reference: TELEPHONE_NUMBERS[0].number,
			clicked: false,
			simulated: true,
		},
	];
}

async function fetchSessions() {
	try {
		const response = await fetch("/api/aokplus/connectable-sessions", {
			method: "GET",
			headers: {
				accept: "application/json",
			},
		});

		if (!response.ok) {
			console.error("Failed to fetch sessions:", response.statusText);
			return;
		}

		const data = await response.json();

		const newSessions = data
			.filter((session: any) => !activeSessions.value.some((s) => s.id === session.id))
			.filter((session: any) => session.reference && TELEPHONE_NUMBERS.some((t) => session.reference == t.number))
			.map((session: any) => ({
				...session,
				clicked: false,
			}));

		activeSessions.value = [
			...activeSessions.value.filter((s) => s.simulated || data.some((session: any) => session.id === s.id)),
			...newSessions,
		];
	} catch (err) {
		console.error("Error fetching sessions:", err);
	}
}

async function sessionClick(sessionId: string, label: string) {
	const session = activeSessions.value.find((s) => s.id === sessionId);
	if (!session) return;

	session.clicked = true;

	// Simulated call (dev only): show the click feedback without creating a real workspace.
	if (session.simulated) {
		loading.value = true;
		setTimeout(() => (loading.value = false), 800);
		return;
	}

	loading.value = true;

	const workspaceId = "new_" + sessionId;
	const name = `Anruf von ${label}${
		" an " + TELEPHONE_NUMBERS.find((t) => t.number === session.reference)?.label || ""
	}`;
	const description = `Erstellt am ${new Date().toLocaleString("de-DE", { timeZone: "Europe/Berlin" })} von ${
		domainStore.account.firstname
	} ${domainStore.account.lastname}`;

	const url = `/api/v1.0/${DOMAIN_ID}/external/newWorkspace`;
	const body = {
		dataspaceId: INSTANCE_DATASPACE_ID,
		templateId: TEMPLATE_WORKSPACE_ID,
		name,
		description,
		id: workspaceId,
	};

	const workspaceRoute = `/${DOMAIN_ID}/template/${INSTANCE_DATASPACE_ID}/template/${workspaceId}`;

	try {
		const response = await fetch(url, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${domainStore.authToken}`,
			},
			body: JSON.stringify(body),
		});

		if (response.ok) {
			const data = await response.json();
			console.log("Workspace created:", data);
			router.push(workspaceRoute);
		} else if (response.status === 409) {
			// Workspace already exists — just open it
			console.info(`Workspace ${workspaceId} already exists, opening it.`);
			router.push(workspaceRoute);
		} else {
			console.error(`Workspace creation failed with status ${response.status}`);
		}
	} catch (err) {
		console.error("Error creating workspace:", err);
	} finally {
		loading.value = false;
	}
}

onMounted(async () => {
	document.body.classList.add("ipad-kinas-page");

	try {
		await domainStore.loadDomain(DOMAIN_ID);
		if (!hasEditorAccess.value) {
			throw new Error("No editor access");
		}
	} catch (error) {
		router.push({ name: "Domains" });
	}
	try {
		await fetchSessions();
		setInterval(fetchSessions, 2_000);
	} catch (error) {
		console.error("Error fetching sessions:", error);
	}
});

onUnmounted(() => {
	document.body.classList.remove("ipad-kinas-page");
	mediaQuery.removeEventListener("change", handleSystemThemeChange);
});
</script>

<style scoped lang="scss">
/* ============================================================
   Design tokens
   ============================================================ */
.landing {
	--brand: #4cb6ac;
	--brand-light: #6fd3c8;
	--brand-glow: rgba(76, 182, 172, 0.55);
	--accent: #2dd4bf;

	--ink: #122220;
	--muted: #5f7370;
	--surface: #ffffff;
	--surface-2: #eef6f5;
	--border: rgba(76, 182, 172, 0.16);
	--page: #eef5f4;

	min-height: 100vh;
	display: flex;
	flex-direction: column;
	position: relative;
	background: var(--page);
	color: var(--ink);
	font-family: "Inter", "Helvetica Neue", system-ui, sans-serif;
	overflow-x: hidden;

	&.is-dark {
		--ink: #e9f5f3;
		--muted: #8aa4a0;
		--surface: rgba(255, 255, 255, 0.04);
		--surface-2: rgba(255, 255, 255, 0.06);
		--border: rgba(255, 255, 255, 0.1);
		--page: #05130f;
	}
}

/* ============================================================
   Theme toggle
   ============================================================ */
.theme-toggle-btn {
	position: fixed;
	top: 18px;
	right: 18px;
	z-index: 100;
	color: #fff;
	background: rgba(255, 255, 255, 0.12);
	backdrop-filter: blur(8px);
	border: 1px solid rgba(255, 255, 255, 0.18);

	&:hover {
		background: rgba(255, 255, 255, 0.22);
	}
}

.sim-call-btn {
	position: fixed;
	left: 18px;
	bottom: 18px;
	z-index: 100;
	text-transform: none;
	letter-spacing: 0;
	box-shadow: 0 6px 20px -6px rgba(0, 0, 0, 0.4);
}

/* ============================================================
   Hero
   ============================================================ */
.hero {
	position: relative;
	min-height: 42vh;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	text-align: center;
	padding: 3.5rem 1.5rem 4rem;
	background: radial-gradient(120% 120% at 50% 0%, #0c5049 0%, #073631 45%, #03201d 100%);
	overflow: hidden;
}

.hero__aurora {
	position: absolute;
	inset: 0;
	z-index: 0;
	filter: blur(55px);

	.orb {
		position: absolute;
		border-radius: 50%;
		opacity: 0.8;
	}
	.orb--1 {
		width: 38vw;
		height: 38vw;
		left: -4vw;
		top: -8vw;
		background: radial-gradient(circle, #2dd4bf, transparent 68%);
		animation: float-a 11s ease-in-out infinite;
	}
	.orb--2 {
		width: 32vw;
		height: 32vw;
		right: -2vw;
		top: -6vw;
		background: radial-gradient(circle, #38bdf8, transparent 68%);
		animation: float-b 14s ease-in-out infinite;
	}
	.orb--3 {
		width: 30vw;
		height: 30vw;
		left: 36vw;
		bottom: -12vw;
		background: radial-gradient(circle, #818cf8, transparent 68%);
		animation: float-c 12.5s ease-in-out infinite;
	}
}

.hero__grid {
	position: absolute;
	inset: 0;
	z-index: 1;
	background-image: linear-gradient(rgba(255, 255, 255, 0.04) 1px, transparent 1px),
		linear-gradient(90deg, rgba(255, 255, 255, 0.04) 1px, transparent 1px);
	background-size: 48px 48px;
	mask-image: radial-gradient(circle at 50% 40%, #000 0%, transparent 75%);
	-webkit-mask-image: radial-gradient(circle at 50% 40%, #000 0%, transparent 75%);
}

.hero__content {
	position: relative;
	z-index: 2;
	max-width: 860px;
	animation: rise 0.9s cubic-bezier(0.16, 1, 0.3, 1) both;
}

.eyebrow {
	display: inline-block;
	padding: 0.45rem 1rem;
	border-radius: 999px;
	background: rgba(255, 255, 255, 0.08);
	border: 1px solid rgba(255, 255, 255, 0.16);
	color: rgba(255, 255, 255, 0.85);
	font-size: 0.8rem;
	font-weight: 600;
	letter-spacing: 0.08em;
	text-indent: 0.08em; // compensate trailing letter-spacing so text is optically centered
	text-transform: uppercase;
	backdrop-filter: blur(8px);
	margin-bottom: 1.6rem;
}

.hero__title {
	font-family: "STEPS", "Inter", sans-serif;
	font-weight: 800;
	font-size: clamp(2.8rem, 8vw, 5.6rem);
	line-height: 0.98;
	letter-spacing: -0.02em;
	margin: 0;
	color: #fff;

	&-line {
		display: block;
	}
	&-accent {
		background: linear-gradient(100deg, #5eead4 0%, #4cb6ac 50%, #0d9488 100%);
		-webkit-background-clip: text;
		background-clip: text;
		-webkit-text-fill-color: transparent;
	}
}

.hero__subtitle {
	margin: 1.6rem auto 0;
	max-width: 560px;
	font-size: clamp(1rem, 2.2vw, 1.25rem);
	line-height: 1.6;
	color: rgba(255, 255, 255, 0.72);
}

/* ============================================================
   Status dock (hero → body bridge)
   ============================================================ */
.status-dock {
	position: relative;
	z-index: 3;
	display: flex;
	justify-content: center;
	margin-top: -1.6rem;
	padding: 0 1.5rem;
}

.status-pill {
	display: inline-flex;
	align-items: center;
	gap: 0.7rem;
	padding: 0.8rem 1.7rem;
	border-radius: 999px;
	background: var(--surface);
	border: 1px solid var(--border);
	box-shadow: 0 18px 50px -18px var(--brand-glow), 0 4px 14px rgba(0, 0, 0, 0.1);
	backdrop-filter: blur(16px);
	font-size: 0.95rem;
	font-weight: 600;
	letter-spacing: 0.01em;
	color: var(--ink);

	&__dot {
		width: 9px;
		height: 9px;
		border-radius: 50%;
		background: var(--muted);
		flex: none;
		transition: background 0.3s ease;
	}

	&.is-live {
		.status-pill__dot {
			background: #22c55e;
			box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.55);
			animation: pulse 2s infinite;
		}
	}
}

/* ============================================================
   Content
   ============================================================ */
.content {
	width: 100%;
	max-width: 1180px;
	margin: 0 auto;
	padding: 3.5rem 1.5rem 2rem;
}

/* ---- live calls ---- */
.livecall {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 4rem;
}

.glass-card {
	width: 100%;
	max-width: 560px;
	border-radius: 22px;
	background: var(--surface);
	border: 1px solid var(--border);
	box-shadow: 0 18px 50px -28px rgba(0, 0, 0, 0.4);
	backdrop-filter: blur(16px);
	padding: 1.4rem;
}

.sessions {
	&__head {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		font-weight: 700;
		font-size: 1.05rem;
		margin-bottom: 0.8rem;
		color: var(--ink);
	}

	&__list {
		max-height: 44vh;
		overflow-y: auto;
		overflow-x: hidden;
		padding: 16px 10px;
	}

	&__empty {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 0.6rem;
		padding: 2rem 1rem;
		color: var(--muted);
		font-size: 0.95rem;
	}
}

.session-item {
	position: relative;
	border-radius: 14px;
	margin-bottom: 0.4rem;
	min-height: 76px;
	cursor: pointer;
	transition: background 0.25s ease, transform 0.25s ease, box-shadow 0.25s ease;

	&:hover {
		background: rgba(76, 182, 172, 0.1);
		transform: translateX(4px);
		box-shadow: 0 10px 26px -16px var(--brand-glow);
	}

	&__title {
		font-weight: 600;
	}
}

/* ---- QR ---- */
.qr-block {
	width: 100%;
	text-align: center;

	&__title {
		font-size: 1.6rem;
		font-weight: 800;
		letter-spacing: -0.02em;
		color: var(--ink);
		margin: 0 0 0.8rem;
	}

	&__text {
		max-width: 620px;
		margin: 0 auto 2.4rem;
		font-size: 1rem;
		line-height: 1.6;
		color: var(--muted);
	}
}

.qr-grid {
	display: flex;
	flex-wrap: wrap;
	justify-content: center;
	gap: 2rem;
}

.qr-card {
	display: flex;
	flex-direction: column;
	align-items: center;
	padding: 1.4rem;
	border-radius: 22px;
	background: var(--surface);
	border: 1px solid var(--border);
	box-shadow: 0 18px 40px -24px rgba(0, 0, 0, 0.35);
	transition: transform 0.35s ease, box-shadow 0.35s ease;

	&:hover {
		transform: translateY(-5px);
		box-shadow: 0 26px 50px -26px var(--brand-glow);
	}

	&__frame {
		padding: 0.85rem;
		border-radius: 16px;
		background: #fff;
		box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.05);
	}

	&__img {
		width: 150px;
		height: 150px;
		display: block;

		@media (max-width: 690px) {
			width: 120px;
			height: 120px;
		}
	}

	&__label {
		margin-top: 1rem;
		font-weight: 700;
		font-size: 1.05rem;
		color: var(--ink);
	}

	&__number {
		margin-top: 0.25rem;
		font-family: "SF Mono", ui-monospace, monospace;
		font-size: 0.85rem;
		color: var(--muted);
	}
}

/* ============================================================
   Logos
   ============================================================ */
.logos {
	padding: 0 1.5rem 2rem;

	&__inner {
		display: flex;
		flex-wrap: wrap;
		justify-content: center;
		align-items: center;
		gap: 2.5rem;
	}
}

.logo-chip {
	display: grid;
	place-items: center;
	width: 240px;
	height: 116px;
	padding: 1.4rem;
	border-radius: 20px;
	background: #fff;
	border: 1px solid var(--border);
	box-shadow: 0 14px 34px -20px rgba(0, 0, 0, 0.3);
	transition: transform 0.35s ease, box-shadow 0.35s ease;

	&:hover {
		transform: translateY(-4px);
		box-shadow: 0 22px 44px -22px rgba(0, 0, 0, 0.4);
	}

	&__img {
		max-width: 100%;
		max-height: 64px;
		height: auto;
		object-fit: contain;
	}

	@media (max-width: 690px) {
		width: 200px;
		height: 100px;
	}
}

/* ============================================================
   Footer
   ============================================================ */
.footer {
	margin-top: auto;
	padding: 2.6rem 1.5rem 2.2rem;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 1.1rem;
	background: linear-gradient(180deg, transparent, rgba(76, 182, 172, 0.06)),
		radial-gradient(120% 80% at 50% 100%, #0c5049 0%, #073631 70%);
	color: rgba(255, 255, 255, 0.85);

	&__socials {
		display: flex;
		gap: 0.8rem;
	}

	&__social {
		display: grid;
		place-items: center;
		width: 46px;
		height: 46px;
		border-radius: 14px;
		border: 1px solid rgba(255, 255, 255, 0.14);
		background: rgba(255, 255, 255, 0.06);
		color: #fff;
		cursor: pointer;
		transition: transform 0.25s ease, background 0.25s ease;

		img {
			width: 22px;
			height: 22px;
			filter: brightness(0) invert(1);
		}

		&:hover {
			transform: translateY(-3px);
			background: rgba(255, 255, 255, 0.16);
		}
	}

	&__copy {
		font-size: 0.9rem;
		color: rgba(255, 255, 255, 0.7);

		strong {
			color: #fff;
		}
	}
}

/* ============================================================
   Animations
   ============================================================ */
@keyframes rise {
	from {
		opacity: 0;
		transform: translateY(24px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

@keyframes float-a {
	0%,
	100% {
		transform: translate(0, 0) scale(1);
	}
	33% {
		transform: translate(14vw, 8vw) scale(1.25);
	}
	66% {
		transform: translate(7vw, 16vw) scale(0.95);
	}
}
@keyframes float-b {
	0%,
	100% {
		transform: translate(0, 0) scale(1);
	}
	33% {
		transform: translate(-12vw, 12vw) scale(0.85);
	}
	66% {
		transform: translate(-18vw, 4vw) scale(1.3);
	}
}
@keyframes float-c {
	0%,
	100% {
		transform: translate(0, 0) scale(1);
	}
	33% {
		transform: translate(12vw, -10vw) scale(1.2);
	}
	66% {
		transform: translate(-8vw, -4vw) scale(0.9);
	}
}

@keyframes pulse {
	0% {
		box-shadow: 0 0 0 0 rgba(74, 222, 128, 0.6);
	}
	70% {
		box-shadow: 0 0 0 10px rgba(74, 222, 128, 0);
	}
	100% {
		box-shadow: 0 0 0 0 rgba(74, 222, 128, 0);
	}
}

/* Incoming-call badge: gradient circle with radar-style ripple rings */
.call-pulse {
	position: relative;
	display: grid;
	place-items: center;
	width: 40px;
	height: 40px;
	margin-inline-end: 1rem;
	border-radius: 50%;
	flex: none;
	color: #fff;
	background: linear-gradient(135deg, #34d399, #059669);
	box-shadow: 0 6px 16px -4px rgba(5, 150, 105, 0.55);

	&__ring {
		position: absolute;
		inset: 0;
		border-radius: 50%;
		border: 2px solid #34d399;
		animation: call-ripple 1.8s cubic-bezier(0.25, 0.6, 0.4, 1) infinite;
	}

	&__ring--delay {
		animation-delay: 0.9s;
	}

	.v-icon {
		position: relative;
		z-index: 1;
		animation: call-nudge 1.8s ease-in-out infinite;
	}
}

@keyframes call-ripple {
	0% {
		transform: scale(1);
		opacity: 0.7;
	}
	100% {
		transform: scale(1.55);
		opacity: 0;
	}
}

@keyframes call-nudge {
	0%,
	100% {
		transform: rotate(0deg);
	}
	10% {
		transform: rotate(-12deg);
	}
	20% {
		transform: rotate(12deg);
	}
	30% {
		transform: rotate(-8deg);
	}
	40% {
		transform: rotate(0deg);
	}
}

@media (prefers-reduced-motion: reduce) {
	.orb,
	.status-pill__dot,
	.call-pulse__ring,
	.call-pulse .v-icon,
	.hero__content {
		animation: none !important;
	}
}
</style>
